export type RetryTest = (
  scope: RetryScope,
  result: unknown,
) => { message: string; retry: boolean };

export type RetryOptions = {
  exponentialBackoffBase?: number;
  maxRetry?: number;
  minJitter?: number;
  maxJitter?: number;
  retryTest?: RetryTest;
  logger?: (...data: any[]) => void;
};

export type RetryScope = {
  maxRetry: number;
  tryCount: number;
};

export const retryOnError = (
  _: unknown,
  result: unknown,
): { message: string; retry: boolean } => ({
  message: result instanceof Error ? result.message : '',
  retry: result instanceof Error,
});

/**
 * Truncated exponential backoff, max exponent is 3
 */
export const waitForExponentialBackoff = async (
  base: number,
  failureCount: number,
  minJitter: number,
  maxJitter: number,
) => {
  const min = parseInt(`${minJitter}`, 10);
  const max = Math.max(min, parseInt(`${maxJitter}`, 10));
  return new Promise(resolve => {
    const jitter = Math.floor(Math.random() * (max - min + 1) + min);
    const timeout = base ** Math.min(failureCount, 3) + jitter;
    setTimeout(() => resolve(timeout), timeout);
  });
};

export const configureRetry = (options?: RetryOptions) => {
  const retryOptions = options ?? {};
  const maxRetry = Math.min(retryOptions.maxRetry ?? 3, 10);
  const minJitter = Math.max(retryOptions.minJitter ?? 100, 100);
  const maxJitter = Math.max(retryOptions.maxJitter ?? 300, 300);
  const exponentialBackoffBase = retryOptions.exponentialBackoffBase ?? 15;

  let tryCount = 0;

  const scope: RetryScope = {
    maxRetry,
    tryCount,
  };

  const reachedRetryLimit = () => tryCount === maxRetry;

  const retryTest = retryOptions.retryTest ?? retryOnError;
  const log = retryOptions.logger ?? (() => {});

  const withRetry = <V>(fn: (scope: RetryScope) => Promise<V>) => {
    const retry = async (): Promise<V> => {
      tryCount += 1;
      scope.tryCount = tryCount;
      try {
        const result = await fn(scope);
        if (reachedRetryLimit()) return result;
        const retryInfo = retryTest(scope, result);
        if (retryInfo.retry) {
          log(`Try ${tryCount}:`, retryInfo.message);
          await waitForExponentialBackoff(
            exponentialBackoffBase,
            tryCount,
            minJitter,
            maxJitter,
          );
          return await retry();
        }

        return result;
      } catch (e) {
        if (reachedRetryLimit()) throw e;
        const retryInfo = retryTest(scope, e);
        if (retryInfo.retry) {
          log(`Try ${tryCount}:`, retryInfo.message);
          await waitForExponentialBackoff(
            exponentialBackoffBase,
            tryCount,
            minJitter,
            maxJitter,
          );
          return await retry();
        }
        throw e;
      }
    };

    return retry;
  };

  return { withRetry, scope };
};
